import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { getConnectors, createConnector, updateConnector, testConnector, deleteConnector, updateSearchConfig, updateIngestionConfig, bindMetadata, getConnectorCoverage, getConnectorDbSchema, retroactiveRegister, setEmbeddingProfile } from '@/api/connectors';
import type { CreateConnectorRequest, SearchConfig, IngestionConfig, ConnectorDbSchema, RetroactiveRegisterRequest, SetEmbeddingProfileRequest } from '@/types/connector';
import type { BindingEntry } from '@/types/binding';

export function useConnectors() {
  return useQuery({
    queryKey: queryKeys.connectors.list(),
    queryFn: getConnectors,
  });
}

export function useCreateConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateConnectorRequest) => createConnector(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useUpdateConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateConnectorRequest> }) => updateConnector(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useTestConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => testConnector(id),
    // 0y-a: refresh the connector list so status badge + KPIs update after a
    // successful test, instead of staying "Disconnected" until a manual reload.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useDeleteConnector() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConnector(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useUpdateSearchConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, search_config }: { id: string; search_config: SearchConfig }) =>
      updateSearchConfig(id, search_config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useUpdateIngestionConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ingestion_config }: { id: string; ingestion_config: IngestionConfig }) =>
      updateIngestionConfig(id, ingestion_config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all }),
  });
}

export function useBindMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bindings }: { id: string; bindings: BindingEntry[] }) =>
      bindMetadata(id, bindings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats });
    },
  });
}

export function useConnectorCoverage(id: string) {
  return useQuery({
    queryKey: queryKeys.connectors.detail(id),
    queryFn: () => getConnectorCoverage(id),
    enabled: !!id,
  });
}

export function useConnectorDbSchema(id: string, enabled: boolean) {
  return useQuery<ConnectorDbSchema>({
    queryKey: queryKeys.connectors.dbSchema(id),
    queryFn: () => getConnectorDbSchema(id),
    enabled: !!id && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

// Item C: register a connector's unmanaged vectors as gated resources.
export function useRetroactiveRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RetroactiveRegisterRequest) => retroactiveRegister(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dataCatalog.all });
    },
  });
}

// Item F: declare a connector's query-embedding profile (write-once).
export function useSetEmbeddingProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SetEmbeddingProfileRequest }) =>
      setEmbeddingProfile(id, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.detail(variables.id) });
    },
  });
}
